import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

// Map user roles to their respective dashboard routes
const getDashboardRoute = (role) => {
  switch (role) {
    case 'admin':
      return '/admin';
    case 'examiner':
      return '/examiner';
    case 'examination_center':
    case 'exam_center':
      return '/dashboard/examination-center';
    case 'institutional':
      return '/dashboard/accreditation';
    case 'curriculum':
      return '/dashboard/curriculum';
    case 'candidate':
    default:
      return '/dashboard';
  }
};

export default function LoginPage() {
  const { login, register, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', full_name: '' });

  // Redirect authenticated users to their appropriate dashboard
  if (isAuthenticated && user) {
    return <Navigate to={getDashboardRoute(user.role)} replace />;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await login(loginForm.email, loginForm.password);
      const userRole = response.user?.role || 'candidate';
      const targetRoute = getDashboardRoute(userRole);
      toast.success('Welcome back');
      navigate(targetRoute);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerForm.email, registerForm.password, registerForm.full_name);
      toast.success('Account created successfully');
      navigate('/dashboard'); // New registrations default to candidate dashboard
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-[#F8FAFC] py-16" data-testid="login-page">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_cbeam-portal/artifacts/2eoy3xqe_IMCAC%20%286%29.png"
            alt="IMCAC Logo"
            className="h-14 w-14 mx-auto mb-3 rounded-sm"
          />
          <h1 className="text-2xl font-bold text-[#1B2A4A] font-heading">Candidate Portal</h1>
          <p className="text-sm text-slate-500 mt-1">Access your IMCAC examination dashboard</p>
        </div>

        <Card className="imcac-card">
          <CardContent className="p-6">
            <Tabs defaultValue="login">
              <TabsList className="w-full mb-6 bg-[#F1F5F9]">
                <TabsTrigger value="login" className="flex-1 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="login-tab">
                  Sign In
                </TabsTrigger>
                <TabsTrigger value="register" className="flex-1 data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white rounded-sm" data-testid="register-tab">
                  Register
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email" className="text-sm font-medium text-slate-700">Email Address</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      className="rounded-sm mt-1"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password" className="text-sm font-medium text-slate-700">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        required
                        className="rounded-sm mt-1 pr-10"
                        data-testid="login-password-input"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm" data-testid="login-submit-btn">
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  <div className="text-center mt-4">
                    <button 
                      type="button" 
                      onClick={() => navigate('/forgot-password')}
                      className="text-sm text-[#C5A572] hover:text-[#B09260] hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot your password?
                    </button>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="reg-name" className="text-sm font-medium text-slate-700">Full Name</Label>
                    <Input
                      id="reg-name"
                      placeholder="Dr. John Smith"
                      value={registerForm.full_name}
                      onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                      required
                      className="rounded-sm mt-1"
                      data-testid="register-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-email" className="text-sm font-medium text-slate-700">Email Address</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required
                      className="rounded-sm mt-1"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-password" className="text-sm font-medium text-slate-700">Password</Label>
                    <Input
                      id="reg-password"
                      type="password"
                      placeholder="Create a strong password"
                      value={registerForm.password}
                      onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                      required
                      className="rounded-sm mt-1"
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm" data-testid="register-submit-btn">
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
